import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import api from "../lib/api";
import EventCard from "../components/EventCard";

// ISO date helpers
const isoDate = (d) => d.toISOString().slice(0, 10);
const today = () => isoDate(new Date());
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); };

const DATE_PRESETS = [
    { id: "today", label: "Aujourd'hui", from: () => today(), to: () => today() },
    { id: "weekend", label: "Ce week-end", from: () => {
        const d = new Date(); const dow = d.getDay();
        const offset = dow === 0 ? 0 : dow === 6 ? 0 : (6 - dow);
        const sat = new Date(); sat.setDate(sat.getDate() + offset);
        return isoDate(sat);
    }, to: () => {
        const d = new Date(); const dow = d.getDay();
        const offset = dow === 0 ? 0 : dow === 6 ? 1 : (7 - dow);
        const sun = new Date(); sun.setDate(sun.getDate() + offset);
        return isoDate(sun);
    } },
    { id: "week", label: "Cette semaine", from: () => today(), to: () => addDays(7) },
    { id: "month", label: "Ce mois", from: () => today(), to: () => addDays(30) },
    { id: "all", label: "Tout", from: () => null, to: () => null },
];

export default function Evenements() {
    const [params, setParams] = useSearchParams();
    const [internalEvents, setInternalEvents] = useState([]);
    const [liveEvents, setLiveEvents] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingInt, setLoadingInt] = useState(false);
    const [loadingLive, setLoadingLive] = useState(false);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(false);

    const city = params.get("city") || "";
    const datePreset = params.get("preset") || "week";
    const customFrom = params.get("from") || "";
    const customTo = params.get("to") || "";

    const { from, to } = useMemo(() => {
        if (customFrom || customTo) return { from: customFrom || null, to: customTo || null };
        const p = DATE_PRESETS.find(x => x.id === datePreset) || DATE_PRESETS[2];
        return { from: p.from(), to: p.to() };
    }, [datePreset, customFrom, customTo]);

    useEffect(() => {
        api.get("/cities").then(r => setCities(r.data)).catch(() => {});
    }, []);

    // Internal DB events — curated
    useEffect(() => {
        setLoadingInt(true);
        api.get("/events", { params: { city: city || undefined, upcoming: true, limit: 100 } })
            .then(r => setInternalEvents(r.data))
            .finally(() => setLoadingInt(false));
    }, [city]);

    // Live OpenAgenda
    useEffect(() => {
        setLoadingLive(true); setError("");
        const cityName = city ? (cities.find(c => c.slug === city)?.name) : undefined;
        api.get("/events-live", {
            params: {
                city: cityName,
                date_from: from || undefined,
                date_to: to || undefined,
                limit: 100,
            },
        }).then(r => {
            setLiveEvents(r.data.events || []);
            setTotal(r.data.total_available || 0);
            if (r.data.error) setError("Service OpenAgenda indisponible. Seuls nos événements curés sont affichés.");
        }).catch(() => setError("Service OpenAgenda indisponible"))
          .finally(() => setLoadingLive(false));
    }, [city, from, to, cities]);

    const update = (k, v) => {
        const next = new URLSearchParams(params);
        if (!v) next.delete(k); else next.set(k, v);
        // Reset custom date if preset changed
        if (k === "preset") { next.delete("from"); next.delete("to"); }
        setParams(next);
    };

    // Group events by date
    const grouped = useMemo(() => {
        const map = new Map();
        for (const e of liveEvents) {
            const dk = (e.start || "").slice(0, 10);
            if (!dk) continue;
            if (!map.has(dk)) map.set(dk, []);
            map.get(dk).push(e);
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [liveEvents]);

    return (
        <>
            <SEO
            title="Agenda & événements — Normandie & Picardie"
            description="Agenda en temps réel : 1 300+ événements régionaux (concerts, expos, festivals, brocantes) entre Le Tréport, Cayeux-sur-Mer, Eu, Aumale, Neufchâtel-en-Bray. Données OpenAgenda officielles."
            path="/evenements"
        />
            <div data-testid="evenements-page" className="bg-cp-ivoire min-h-screen">
            {/* HERO */}
            <section className="px-5 sm:px-6 md:px-12 pt-28 md:pt-36 pb-12 md:pb-16 max-w-[1600px] mx-auto">
                <p className="cp-tiny text-cp-foret mb-6">Chapitre III · L'agenda vivant</p>
                <h1 className="cp-serif text-4xl sm:text-5xl md:text-[56px] lg:text-[72px] xl:text-[92px] leading-[1.02] text-cp-encre tracking-[-0.025em] max-w-5xl">
                    Ce qui <em className="cp-display-italic text-cp-foret">se passe</em>,<br />
                    vraiment.
                </h1>
                <p className="cp-serif italic text-lg md:text-2xl text-cp-gris leading-[1.5] mt-8 max-w-3xl">
                    Événements curés par notre rédaction + base publique <em>OpenAgenda</em> (toutes communes du territoire). Filtrez par date, filtrez par commune. Tout est vérifiable — chaque événement public a une URL source.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cp-foret border border-cp-foret/40 px-3 py-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-cp-foret animate-pulse" /> Source : OpenAgenda · {total.toLocaleString("fr-FR")} événements publics recensés sur le territoire
                    </span>
                    <Link
                        to="/soumettre-evenement"
                        data-testid="cta-submit-event"
                        className="inline-flex items-center gap-2 cp-tiny text-cp-encre border border-cp-encre/30 px-3 py-1.5 hover:bg-cp-encre hover:text-cp-ivoire transition-colors"
                    >
                        + Proposer le vôtre
                    </Link>
                </div>
            </section>

            {/* FILTERS */}
            <section className="sticky top-16 z-30 bg-cp-ivoire/95 backdrop-blur border-y border-cp-border py-3 md:py-5" data-testid="evenements-filters">
                <div className="max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12">
                    {/* Mobile trigger */}
                    <button
                        type="button"
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        className="md:hidden w-full flex items-center justify-between py-2"
                        data-testid="filter-toggle"
                    >
                        <span className="cp-kicker text-[11px] text-cp-encre">
                            Filtres · <span className="text-cp-sable-deep">{datePreset === 'week' ? "Cette semaine" : datePreset === 'today' ? "Aujourd'hui" : datePreset === 'month' ? "Ce mois" : "À venir"}</span>
                        </span>
                        <span className={`transition-transform duration-200 text-cp-foret cp-serif italic text-lg ${filtersOpen ? "rotate-90" : ""}`}>→</span>
                    </button>

                    <div className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-wrap items-center gap-3 md:gap-5 pt-3 md:pt-0`}>
                        {/* Date presets */}
                        <div className="flex flex-wrap gap-2">
                            {DATE_PRESETS.map(p => (
                                <button key={p.id} onClick={() => update("preset", p.id)}
                                    data-testid={`date-preset-${p.id}`}
                                    className={`cp-tiny px-4 py-2 border transition-all ${datePreset === p.id && !customFrom ? "bg-cp-foret text-cp-ivoire border-cp-foret" : "bg-cp-ivoire text-cp-gris border-cp-border hover:border-cp-foret"}`}>
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <div className="hidden md:block w-px h-6 bg-cp-border" />

                        {/* Custom range */}
                        <div className="flex items-center gap-2">
                            <input type="date" value={customFrom}
                                onChange={(e) => update("from", e.target.value)}
                                data-testid="date-from-input"
                                className="cp-tiny px-3 py-1.5 border border-cp-border bg-cp-ivoire text-cp-encre" />
                            <span className="cp-tiny text-cp-gris">→</span>
                            <input type="date" value={customTo}
                                onChange={(e) => update("to", e.target.value)}
                                data-testid="date-to-input"
                                className="cp-tiny px-3 py-1.5 border border-cp-border bg-cp-ivoire text-cp-encre" />
                        </div>

                        <div className="hidden md:block w-px h-6 bg-cp-border" />

                        <select value={city} onChange={(e) => update("city", e.target.value)}
                            data-testid="city-select"
                            className="cp-tiny px-3 py-2 border border-cp-border bg-cp-ivoire text-cp-encre">
                            <option value="">Toutes les communes</option>
                            {cities.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            {/* CURATED — our editorial picks */}
            {internalEvents.length > 0 && (
                <section className="py-16 md:py-20 max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12" data-testid="curated-events">
                    <div className="flex items-baseline justify-between mb-10">
                        <div>
                            <p className="cp-tiny text-cp-terre mb-2">Curés par la rédaction</p>
                            <h2 className="cp-serif text-3xl md:text-5xl text-cp-encre tracking-[-0.02em]">
                                <em className="cp-display-italic">Nos choix.</em>
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                        {internalEvents.slice(0, 6).map(e => <EventCard key={e.id} event={e} />)}
                    </div>
                </section>
            )}

            {/* LIVE OpenAgenda grouped by date */}
            <section className="bg-cp-craie py-16 md:py-24 border-t border-cp-border" data-testid="live-events">
                <div className="max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12">
                    <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <p className="cp-tiny text-cp-foret mb-2">Source publique · OpenAgenda</p>
                            <h2 className="cp-serif text-3xl md:text-5xl text-cp-encre tracking-[-0.02em]">
                                <em className="cp-display-italic">L'agenda, minute par minute.</em>
                            </h2>
                        </div>
                        <p className="cp-tiny text-cp-gris max-w-sm">{liveEvents.length} événements trouvés · les horaires et liens pointent vers la source officielle</p>
                    </div>

                    {error && <p className="cp-tiny text-cp-terre mb-8" data-testid="live-error">{error}</p>}
                    {loadingLive && <p className="cp-serif italic text-cp-gris">Chargement de l'agenda…</p>}
                    {!loadingLive && grouped.length === 0 && (
                        <p className="cp-serif italic text-cp-gris max-w-xl">Aucun événement pour cette période dans OpenAgenda. Essayez une plage plus large ou une autre commune.</p>
                    )}

                    <div className="space-y-14">
                        {grouped.map(([dk, items]) => (
                            <motion.div key={dk}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5 }}
                                data-testid={`date-group-${dk}`}>
                                <div className="flex items-baseline gap-4 mb-6 border-b border-cp-border pb-3">
                                    <span className="cp-serif italic text-4xl md:text-5xl text-cp-terre">
                                        {new Date(dk).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                                    </span>
                                    <span className="cp-tiny text-cp-gris">
                                        {new Date(dk).toLocaleDateString("fr-FR", { weekday: "long", year: "numeric" })}
                                    </span>
                                    <span className="ml-auto cp-tiny text-cp-gris">{items.length} événement{items.length > 1 ? "s" : ""}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                    {items.map(e => (
                                        <a key={e.uid} href={e.url} target="_blank" rel="noopener noreferrer"
                                            data-testid={`live-event-${e.uid}`}
                                            className="group block bg-cp-ivoire border border-cp-border hover:border-cp-foret transition-colors p-6">
                                            <div className="flex items-baseline justify-between gap-3 mb-3">
                                                <span className="cp-tiny text-cp-terre">{e.city || "—"}</span>
                                                <span className="cp-tiny text-cp-gris/70">
                                                    {e.start ? new Date(e.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                                                </span>
                                            </div>
                                            <h3 className="cp-serif text-xl md:text-2xl leading-tight text-cp-encre mb-2 group-hover:text-cp-foret transition-colors">
                                                {e.title}
                                            </h3>
                                            {e.venue && <p className="cp-tiny text-cp-gris mb-2">{e.venue}</p>}
                                            {e.description && <p className="cp-serif italic text-sm text-cp-gris leading-snug line-clamp-3">{e.description}</p>}
                                            <span className="cp-tiny text-cp-foret mt-4 inline-block">Source ↗</span>
                                        </a>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <p className="cp-tiny text-cp-gris/70 mt-16 max-w-2xl cp-serif italic">
                        Source ouverte — <em>Événements publics OpenAgenda</em> via Opendatasoft (data.gouv.fr). Nous n'hébergeons pas ces données, nous les affichons pour vous. Tous les liens « Source » renvoient vers l'organisateur officiel.
                    </p>
                </div>
            </section>
        </div>
    </>

    );
}
